import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { inject, observer } from 'mobx-react';
import Webcam from 'react-webcam';
import Fullscreen from 'react-full-screen';
import BarcodeReader from 'react-barcode-reader';
import { toast, cssTransition } from 'react-toastify';

import 'animate.css/animate.min.css';
import 'react-toastify/dist/ReactToastify.css';

import LiveDispatchHeader from './LiveDispatchHeader';
import Orders from './Orders';
import Loading from '../Loading';
import ScanningBox from '../ScanningBox';
import Modal from '../Modal';
import SKUItem from '../common/SKUItem';
import * as api from '../../services/api';
import OrderWebSocket from '../../services/orderWebSocket';
import InstaServiceProduction from './InstaServiceProduction';
import CustomToastContainer from './CustomToastContainer';
import DriveThrough from './DriveThrough';
import { isMobileViewport } from '../../services/device';

const base = 'live-dispatch';
const DITA_UPSELL_POLLING_INTERVAL_MS = 1000 * 60;
const audio = new Audio(`${process.env.PUBLIC_URL}/alert_store_closed.mp3`);
const driveThroughAudio = new Audio(`${process.env.PUBLIC_URL}/drive-through-alert.mp3`);
driveThroughAudio.loop = true;

@inject('LiveDispatchStore', 'ScanningStore', 'UserStore', 'ProductionStore', 'RiderAndOutletPartnerStore')
@observer
class PackingV2 extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isFirstMount: true,
            webSocketConnected: true,
            selectedOrder: null,
            actionLoading: false,
            actionErrorMessage: '',
            outletControlErrorMessage: '',
            lastScannedItem: null,
            bagReprintLoading: false,
            ditaUpsellAddons: [],
            ditaUpsellAvailabilityByMenuItemId: {},
            ditaUpsellAvailabilityBySku: {},
            ditaUpsellLoading: false,
            ditaUpsellError: '',
            ditaUpsellSkippedOrderNumbers: {},
            ditaUpsellCreatingOrderNumbers: {},
            ditaUpsellCreatedOrderNumbers: {},
            isOutletControlsOpen: false,
            outletControlsTab: 'dispatches',
            selectedDispatchNumbers: [],
            closingDispatchReason: '',
        };

        this.webcamRef = React.createRef();
        this.pollingInterval = null;
        this.staffInterval = null;
        this.ditaUpsellPollingInterval = null;
        this.ditaUpsellRankingRequest = null;
        this.isDitaUpsellFetching = false;
    }

    componentDidMount() {
        const {
            UserStore: { handleSetFullscreen },
            LiveDispatchStore: { handleGetDispatches, handleGetDineInOrders, handleGetAggregatorStoreStatus },
            RiderAndOutletPartnerStore: { getActiveStaffMembers },
        } = this.props;

        if (this.state.isFirstMount) {
            if (!isMobileViewport()) {
                handleSetFullscreen(true);
            }
            this.setState({ isFirstMount: false });
        }

        handleGetDispatches(true);
        handleGetDineInOrders(true);
        this.startDitaUpsellPolling();
        getActiveStaffMembers();
        handleGetAggregatorStoreStatus();
        this.staffInterval = setInterval(() => {
            getActiveStaffMembers();
            handleGetAggregatorStoreStatus();
        }, 1000 * 60);

        window.addEventListener('keydown', this.handleScanPopup);
    }

    componentDidUpdate() {
        const {
            LiveDispatchStore: { scannedItem },
        } = this.props;
        const { lastScannedItem } = this.state;

        if (scannedItem && scannedItem.item && lastScannedItem !== scannedItem.item) {
            this.setState({ lastScannedItem: scannedItem.item });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleScanPopup);
        this.stopPolling();
        if (this.staffInterval) {
            clearInterval(this.staffInterval);
            this.staffInterval = null;
        }
        this.stopDitaUpsellPolling();
    }

    startDitaUpsellPolling = () => {
        if (this.ditaUpsellPollingInterval) {
            return;
        }

        this.fetchDitaUpsellAddons();
        this.ditaUpsellPollingInterval = setInterval(() => {
            this.fetchDitaUpsellAddons({ silent: true });
        }, DITA_UPSELL_POLLING_INTERVAL_MS);
    };

    stopDitaUpsellPolling = () => {
        if (this.ditaUpsellPollingInterval) {
            clearInterval(this.ditaUpsellPollingInterval);
            this.ditaUpsellPollingInterval = null;
        }
    };

    handleClickOpen = (store) => {
        const { LiveDispatchStore } = this.props;
        LiveDispatchStore.handleOpenStore(store);
    };

    startPolling = () => {
        if (this.pollingInterval) {
            return;
        }

        const {
            LiveDispatchStore: { handleGetDispatches, handleGetDineInOrders },
        } = this.props;

        handleGetDispatches(true);
        handleGetDineInOrders(true);

        this.pollingInterval = setInterval(() => {
            handleGetDispatches(false);
            handleGetDineInOrders(false);
        }, 1000 * 10);
    };

    stopPolling = () => {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    };

    handleWebSocketConnectionChange = (connected) => {
        this.setState({ webSocketConnected: connected });

        if (!connected) {
            this.startPolling();
        } else {
            this.stopPolling();
        }
    };

    handleScanPopup = () => {
        const { LiveDispatchStore } = this.props;
        LiveDispatchStore.handleScanPopup();
    };

    handleClick = (event) => {
        if (
            event.target &&
            event.target.className &&
            event.target.className.indexOf('dispatch_scan') === -1
        ) {
            this.handleScanPopup();
        }
    };

    handleScanWrapper = (scannedBarcode) => {
        const { LiveDispatchStore, ScanningStore } = this.props;
        const {
            setItemImage,
            handledScannedItem,
            handledScannedBag,
            handledScannedWarmerItem,
            displayErrorResultFunc,
        } = LiveDispatchStore;
        const { handleScan } = ScanningStore;

        if (this.webcamRef.current) {
            const image = this.webcamRef.current.getScreenshot();
            setItemImage(image);
        }

        const isBagQRCode = scannedBarcode.split('-').length === 1;

        if (isBagQRCode) {
            handledScannedBag(scannedBarcode);
        } else {
            handleScan(
                scannedBarcode,
                handledScannedItem,
                handledScannedWarmerItem,
                displayErrorResultFunc
            );
        }
    };

    getAggregatorPlatform = (order) => {
        const salePlatform = `${order && order.sale_platform ? order.sale_platform : ''}`.toLowerCase();

        if (salePlatform.includes('grabfood')) {
            return 'grabfood';
        }

        if (salePlatform.includes('foodpanda')) {
            return 'foodpanda';
        }

        if (salePlatform.includes('shopeefood')) {
            return 'shopeefood';
        }

        const aggregatorOrderNumber = `${order && order.aggregator_order_number ? order.aggregator_order_number : ''}`.toUpperCase();

        if (aggregatorOrderNumber.startsWith('GF-')) {
            return 'grabfood';
        }

        if (aggregatorOrderNumber.startsWith('FP-') || aggregatorOrderNumber.startsWith('PF-')) {
            return 'foodpanda';
        }

        if (aggregatorOrderNumber.startsWith('SF-') || aggregatorOrderNumber.startsWith('SP-')) {
            return 'shopeefood';
        }

        return null;
    };

    isAggregatorOrder = (order) => !!this.getAggregatorPlatform(order);

    isDineInOrder = (order) => {
        return (
            (order && order.pick_up_type === 'DINE_IN') ||
            (order && order.order_mode === 'DINE_IN') ||
            (order && order.sale_platform === 'Dine In')
        );
    };

    isSelfPickupOrder = (order) => {
        return (
            (order && order.order_mode === 'PANDU_MASUK') ||
            (order && order.order_mode === 'PANDU_LALU') ||
            (order && order.customer_self_pickup_at_outlet === true)
        );
    };

    isPanduOrder = (order) => {
        return (
            order &&
            (order.order_mode === 'PANDU_MASUK' || order.order_mode === 'PANDU_LALU')
        );
    };

    getPanduCarDescription = (order) => {
        if (!order || !order.car_details) {
            return '';
        }

        return [order.car_details.color, order.car_details.make]
            .filter(Boolean)
            .join(' ');
    };

    isPanduMasukCustomerWaiting = (order) => {
        const {
            LiveDispatchStore: { newPanduMasukOrder },
        } = this.props;

        if (!order || order.order_mode !== 'PANDU_MASUK' || order.bag_number == null) {
            return false;
        }

        return (newPanduMasukOrder || []).some(
            (bagNumber) => `${bagNumber}` === `${order.bag_number}`
        );
    };

    isTakeawayOrder = (order) => {
        if (this.isDineInOrder(order) || this.isAggregatorOrder(order) || this.isSelfPickupOrder(order)) {
            return false;
        }

        return (
            (order && order.pick_up_type === 'TAKE_AWAY') ||
            (order && order.order_mode === 'TAKE_AWAY') ||
            (order && order.order_mode === 'PICK_UP') ||
            (order && order.sale_platform === 'Pick Up')
        );
    };

    getOrderType = (order) => {
        if (this.isAggregatorOrder(order)) {
            const aggregatorPlatform = this.getAggregatorPlatform(order);

            if (aggregatorPlatform === 'grabfood') {
                return { key: 'aggregator', label: 'GrabFood' };
            }

            if (aggregatorPlatform === 'foodpanda') {
                return { key: 'aggregator', label: 'FoodPanda' };
            }

            if (aggregatorPlatform === 'shopeefood') {
                return { key: 'aggregator', label: 'ShopeeFood' };
            }

            return { key: 'aggregator', label: 'Aggregator' };
        }

        if (order && order.order_mode === 'PANDU_MASUK') {
            return { key: 'self-pickup', label: 'Pandu Masuk' };
        }

        if (order && order.order_mode === 'PANDU_LALU') {
            return { key: 'self-pickup', label: 'Pandu Lalu' };
        }

        if (this.isSelfPickupOrder(order)) {
            return { key: 'self-pickup', label: 'Self Pickup' };
        }

        if (this.isDineInOrder(order)) {
            return { key: 'dine-in', label: 'Dine In' };
        }

        if (this.isTakeawayOrder(order)) {
            return { key: 'takeaway', label: 'Takeaway' };
        }

        return { key: 'delivery', label: 'Delivery' };
    };

    getAggregatorCustomerName = (order) => {
        if (!order) {
            return '';
        }

        const aggregatorId = order.aggregator_order_id
            ? `${order.aggregator_order_id}`.trim().toLowerCase()
            : '';

        const isAggregatorReferenceValue = (value) => {
            const normalizedValue = `${value}`.trim();
            if (!normalizedValue) {
                return true;
            }

            const normalizedLower = normalizedValue.toLowerCase();
            if (aggregatorId && normalizedLower === aggregatorId) {
                return true;
            }

            return /^(GF|FP|PF|SF|SP)[-_]?\d+$/i.test(normalizedValue);
        };

        const rawCandidates = [
            order.customer_display_name,
            order.customer_full_name,
            order.recipient_name,
            order.receiver_name,
            order.customer && order.customer.alias,
            order.customer && order.customer.name,
            order.customer && order.customer.full_name,
            order.customer && order.customer.display_name,
            order.alias,
            order.customer_name,
        ];

        const candidates = rawCandidates
            .map((value) => (value == null ? '' : `${value}`.trim()))
            .filter((value, index, arr) => value.length > 0 && arr.indexOf(value) === index);

        if (candidates.length === 0) {
            return '';
        }

        const customerCandidate = candidates.find((value) => !isAggregatorReferenceValue(value));
        if (customerCandidate) {
            return customerCandidate;
        }

        return '';
    };

    getSummaryCustomerName = (order) => {
        if (!order) {
            return '-';
        }

        if (!this.isAggregatorOrder(order)) {
            return order.customer_name || '-';
        }

        const aggregatorCustomerName = this.getAggregatorCustomerName(order);
        if (aggregatorCustomerName) {
            return aggregatorCustomerName;
        }

        return '-';
    };

    getCustomerFirstName = (order) => {
        const customerName = `${this.getSummaryCustomerName(order) || ''}`.trim();
        if (!customerName || customerName === '-') {
            return '';
        }

        return customerName.split(/\s+/)[0];
    };

    formatDitaUpsellScript = (order, script) => {
        if (!script) {
            return '';
        }

        const firstName = this.getCustomerFirstName(order);
        if (!firstName) {
            return script;
        }

        return `${firstName}, ${script.charAt(0).toLowerCase()}${script.slice(1)}`;
    };

    normalizeWhatsappPhoneNumber = (phoneNumber) => {
        const digits = `${phoneNumber || ''}`.replace(/\D/g, '');

        if (!digits) {
            return '';
        }

        if (digits.indexOf('60') === 0) {
            return digits;
        }

        if (digits.indexOf('0') === 0) {
            return `60${digits.slice(1)}`;
        }

        return digits;
    };

    getWhatsappUrl = (phoneNumber) => {
        const normalizedPhoneNumber = this.normalizeWhatsappPhoneNumber(phoneNumber);

        return normalizedPhoneNumber ? `https://wa.me/${normalizedPhoneNumber}` : '';
    };

    getWhatsappQrUrl = (phoneNumber) => {
        const whatsappUrl = this.getWhatsappUrl(phoneNumber);

        if (!whatsappUrl) {
            return '';
        }

        return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=${encodeURIComponent(whatsappUrl)}`;
    };

    getAggregatorLogo = (order) => {
        const aggregatorPlatform = this.getAggregatorPlatform(order);

        if (aggregatorPlatform === 'grabfood') {
            return {
                src: 'https://image.popcontent.link/GrabFood-WhiteBG.svg',
                alt: 'GrabFood',
            };
        }

        if (aggregatorPlatform === 'foodpanda') {
            return {
                src: 'https://image.popcontent.link/FoodPanda-WhiteBG.svg',
                alt: 'FoodPanda',
            };
        }

        if (aggregatorPlatform === 'shopeefood') {
            return {
                src: 'https://image.popcontent.link/ShopeeFood-WhiteBG.svg',
                alt: 'ShopeeFood',
            };
        }

        return null;
    };

    isOrderFullyScanned = (order) => {
        if (!order || !order.items || order.items.length === 0) {
            return false;
        }

        return order.items.every((item) => item.status === 'ON_ROUTE');
    };

    getOrderDisplayNumber = (order) => {
        if (order == null) {
            return '-';
        }

        if (order.table_number) {
            return `TABLE ${order.table_number}`;
        }

        if (order.bag_number) {
            return `BAG ${order.bag_number}`;
        }

        if (order.pick_up_number) {
            return `PICKUP ${order.pick_up_number}`;
        }

        if (order.order_number) {
            return order.order_number;
        }

        return '-';
    };

    hasAssignedBagNumber = (order) => {
        if (!order || order.bag_number == null) {
            return false;
        }

        const normalizedBagNumber = String(order.bag_number).trim();
        return normalizedBagNumber !== '' && normalizedBagNumber !== '-';
    };

    shouldHideScheduledSelfPickup = (order) => {
        return this.isSelfPickupOrder(order) && !this.hasAssignedBagNumber(order);
    };

    hasPendingInstaServiceTopup = (instaServiceProductions) => {
        if (!Array.isArray(instaServiceProductions) || instaServiceProductions.length === 0) {
            return false;
        }

        return instaServiceProductions.some((stock) =>
            Array.isArray(stock && stock.requests) &&
            stock.requests.some((request) => `${request && request.status ? request.status : ''}`.toUpperCase() !== 'COMPLETED')
        );
    };

    getSummaryOrderNumber = (order) => {
        if (order == null) {
            return '-';
        }

        if (order.table_number) {
            return `${order.table_number}`;
        }

        if (order.bag_number) {
            return `${order.bag_number}`;
        }

        if (order.pick_up_number) {
            return `${order.pick_up_number}`;
        }

        if (order.order_number) {
            return `${order.order_number}`;
        }

        return '-';
    };

    getOrderTime = (order) => {
        if (!order) {
            return 0;
        }

        return (
            order.production_started_at ||
            order.delivery_time ||
            order.order_date ||
            order.created_date ||
            0
        );
    };

    getOrderTimestampMs = (order) => {
        const rawOrderTime = this.getOrderTime(order);

        if (typeof rawOrderTime === 'number' && Number.isFinite(rawOrderTime)) {
            return rawOrderTime;
        }

        const numericTime = Number(rawOrderTime);
        if (Number.isFinite(numericTime) && numericTime > 0) {
            return numericTime;
        }

        const parsedDate = Date.parse(rawOrderTime);
        if (!Number.isNaN(parsedDate)) {
            return parsedDate;
        }

        return 0;
    };

    isAgedDineInOrTakeaway = (order) => {
        if (!order) {
            return false;
        }

        const isInOutletOrder = this.isDineInOrder(order) || this.isTakeawayOrder(order);
        if (!isInOutletOrder) {
            return false;
        }

        const orderTimestampMs = this.getOrderTimestampMs(order);
        if (!orderTimestampMs) {
            return false;
        }

        const eightMinutesInMs = 8 * 60 * 1000;
        return Date.now() - orderTimestampMs >= eightMinutesInMs;
    };

    formatOrderTime = (order) => {
        if (!order || !order.created_date) {
            return '-';
        }

        const orderDate = new Date(order.created_date);
        if (Number.isNaN(orderDate.getTime())) {
            return '-';
        }

        return orderDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    };

    shouldHighlightOrderStatus = (order) => {
        if (!order) {
            return false;
        }

        return (
            (order.order_mode === 'DELIVERY' &&
                (order.order_status === 'Pending' || order.order_status === 'Unconfirmed')) ||
            (order.order_mode === 'DINE_IN' && order.order_status !== 'Delivered') ||
            (order.order_mode === 'PICK_UP' &&
                !order.bag_scanned_at &&
                order.order_status !== 'Delivered' &&
                order.order_status !== 'Intransit') ||
            order.order_status === 'Cancelled'
        );
    };

    isPaymentDone = (order) => {
        if (!order) {
            return false;
        }

        return (
            order.transaction_status === 'Complete' ||
            (order.payment_method === 'Cash' && order.order_status === 'Delivered')
        );
    };

    getNetTotalDisplay = (order) => {
        if (!order) {
            return '-';
        }

        const netTotal = this.parseCurrencyValue(order.net_total);
        if (netTotal == null) {
            return '-';
        }

        return (netTotal * 1.06).toFixed(2);
    };

    parseCurrencyValue = (value) => {
        if (value == null || value === '') {
            return null;
        }

        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }

        const numericValue = Number(String(value).replace(/[^0-9.-]/g, ''));
        return Number.isFinite(numericValue) ? numericValue : null;
    };

    getOrderAmountDue = (order) => {
        if (!order) {
            return null;
        }

        const explicitAmountDue = this.parseCurrencyValue(order.amount_due);
        if (explicitAmountDue != null) {
            return explicitAmountDue > 0 ? explicitAmountDue : null;
        }

        const paymentPending = order.amount_due === true || order.transaction_status === 'Pending';
        if (!paymentPending) {
            return null;
        }

        const preTaxNetTotal = this.parseCurrencyValue(order.pre_tax_net_total);
        if (preTaxNetTotal == null || preTaxNetTotal <= 0) {
            return null;
        }

        return preTaxNetTotal * 1.06;
    };

    formatCurrencyAmount = (value) => {
        if (value == null) {
            return '-';
        }

        return `RM ${value.toFixed(2)}`;
    };

    getDateStringForAvailableForSale = () => {
        const date = new Date();
        const day = `${date.getDate()}`.padStart(2, '0');
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    getAddonCategory = (item) => {
        return `${item && (item.addon_category || item.category_name || item.menu_item_category) ? (item.addon_category || item.category_name || item.menu_item_category) : ''}`.trim();
    };

    normalizeAddonCategory = (item) => this.getAddonCategory(item).toLowerCase();

    getAddonMenuItemId = (item) => {
        if (!item) {
            return null;
        }

        return item.menu_item_id || item.menuItemId || item.today_menu_id || item.id || null;
    };

    getAddonDisplayName = (item) => {
        if (!item) {
            return '';
        }

        return item.title_bold || item.name || item.sku || item.title || 'add-on';
    };

    getAddonPrice = (item) => {
        if (!item) {
            return null;
        }

        return this.parseCurrencyValue(
            item.value_meal_price ||
            item.special_offer_price ||
            item.discounted_price ||
            item.price ||
            item.original_price
        );
    };

    formatDitaUpsellPrice = (item) => {
        const price = this.getAddonPrice(item);
        if (price == null) {
            return '-';
        }

        return `RM${price.toFixed(2)}`;
    };

    isAddonAvailableForDitaUpsell = (item, availabilityByMenuItemId, availabilityBySku) => {
        if (!item || item.invisible === true || item.active === false || item.is_active === false) {
            return false;
        }

        const quantityLeft = this.parseCurrencyValue(item.quantity_left);
        if (quantityLeft != null && quantityLeft <= 0) {
            return false;
        }

        const menuItemId = this.getAddonMenuItemId(item);
        const availability =
            (menuItemId && availabilityByMenuItemId[menuItemId]) ||
            (item.sku && availabilityBySku[item.sku]);

        if (!availability) {
            return true;
        }

        if (availability.isAlwaysAvailable === true || availability.always_available === true) {
            return true;
        }

        const availabilityFields = [
            availability.availableForSaleQuantity,
            availability.available_for_sale_quantity,
            availability.availableQuantity,
            availability.available_quantity,
            availability.quantityLeft,
            availability.quantity_left,
        ];

        for (let index = 0; index < availabilityFields.length; index += 1) {
            const availableQuantity = this.parseCurrencyValue(availabilityFields[index]);
            if (availableQuantity != null) {
                return availableQuantity > 0;
            }
        }

        return true;
    };

    isDitaUpsellCandidateAvailable = (item) => {
        const { ditaUpsellAvailabilityByMenuItemId, ditaUpsellAvailabilityBySku } = this.state;
        return this.isAddonAvailableForDitaUpsell(
            item,
            ditaUpsellAvailabilityByMenuItemId || {},
            ditaUpsellAvailabilityBySku || {}
        );
    };

    sortDitaUpsellAddons = (addons, rankingByMenuItemId) => {
        return addons.slice().sort((a, b) => {
            const aId = this.getAddonMenuItemId(a);
            const bId = this.getAddonMenuItemId(b);
            const aRank = rankingByMenuItemId[aId] || Number.MAX_SAFE_INTEGER;
            const bRank = rankingByMenuItemId[bId] || Number.MAX_SAFE_INTEGER;

            if (aRank !== bRank) {
                return aRank - bRank;
            }

            const aCurrentRanking = this.parseCurrencyValue(a.current_ranking);
            const bCurrentRanking = this.parseCurrencyValue(b.current_ranking);

            if (aCurrentRanking != null && bCurrentRanking != null && aCurrentRanking !== bCurrentRanking) {
                return aCurrentRanking - bCurrentRanking;
            }

            return this.getAddonDisplayName(a).localeCompare(this.getAddonDisplayName(b));
        });
    };

    getMenuStreamAddons = (menuStreamResponse) => {
        const dates = menuStreamResponse && Array.isArray(menuStreamResponse.dates)
            ? menuStreamResponse.dates
            : [];
        const todayMenu = dates.find((menuDate) => menuDate && menuDate.display_date === 'Today') || dates[0] || {};
        const items = Array.isArray(todayMenu.items) ? todayMenu.items : [];

        return items.filter((item) => item && item.item_type === 'Addons');
    };

    fetchDitaUpsellRanking = () => {
        if (!this.ditaUpsellRankingRequest) {
            this.ditaUpsellRankingRequest = api.getMetabaseKioskAddonUpsellRanking().catch(() => []);
        }

        return this.ditaUpsellRankingRequest;
    };

    getDitaUpsellRankingRows = (rankingResponse, availabilityItems) => {
        const rankings = Array.isArray(rankingResponse)
            ? rankingResponse
            : (rankingResponse && Array.isArray(rankingResponse.rows) ? rankingResponse.rows : []);

        if (rankings.length > 0) {
            return rankings;
        }

        return (availabilityItems || [])
            .filter((item) => this.parseCurrencyValue(item.current_ranking) != null)
            .map((item) => ({
                key: this.getAddonMenuItemId(item),
                value: item.current_ranking,
                menu_item_id: this.getAddonMenuItemId(item),
                menu_item_name: this.getAddonDisplayName(item),
                sku: item.sku,
                addon_category: item.addon_category || item.menu_item_category,
            }));
    };

    fetchDitaUpsellAddons = ({ silent = false } = {}) => {
        if (this.isDitaUpsellFetching) {
            return;
        }

        const {
            UserStore: { selectedHub },
        } = this.props;
        const addressId = selectedHub && selectedHub.pickupAddress && selectedHub.pickupAddress.id;

        this.isDitaUpsellFetching = true;
        if (!silent) {
            this.setState({ ditaUpsellLoading: true, ditaUpsellError: '' });
        }

        Promise.all([
            api.getMenuStream({ addressId, orderMode: 'PICK_UP' }),
            this.fetchDitaUpsellRanking(),
        ])
            .then(([menuStreamResponse, rankingResponse]) => {
                const availabilityItems = this.getMenuStreamAddons(menuStreamResponse);
                const rankings = this.getDitaUpsellRankingRows(rankingResponse, availabilityItems);
                const availabilityByMenuItemId = {};
                const availabilityBySku = {};
                const rankingByMenuItemId = {};
                const rankingByMenuItemIdData = {};

                availabilityItems.forEach((item) => {
                    if (item.menuItemId || item.menu_item_id) {
                        availabilityByMenuItemId[item.menuItemId || item.menu_item_id] = item;
                    }
                    if (item.sku) {
                        availabilityBySku[item.sku] = item;
                    }
                });

                rankings.forEach((ranking, index) => {
                    const menuItemId = ranking && (
                        ranking.key ||
                        ranking.menu_item_id ||
                        ranking.menuItemId ||
                        ranking.today_menu_id ||
                        ranking.id
                    );
                    const rankingValue = ranking && (
                        ranking.value ||
                        ranking.ranking ||
                        ranking.current_ranking ||
                        ranking.rank
                    );

                    if (menuItemId != null) {
                        rankingByMenuItemId[menuItemId] = Number(rankingValue) || index + 1;
                        rankingByMenuItemIdData[menuItemId] = ranking;
                    }
                });

                const addons = availabilityItems
                    .filter((item) => {
                        const menuItemId = this.getAddonMenuItemId(item);
                        return menuItemId && rankingByMenuItemIdData[menuItemId];
                    })
                    .map((item) => {
                        const menuItemId = this.getAddonMenuItemId(item);
                        const ranking = rankingByMenuItemIdData[menuItemId];

                        return {
                            ...ranking,
                            ...item,
                            name: item.name || ranking.menu_item_name || ranking.name,
                            title: item.title || ranking.menu_item_name || ranking.title,
                            addon_category: item.addon_category || item.menu_item_category || ranking.addon_category,
                        };
                    });

                this.setState({
                    ditaUpsellAddons: this.sortDitaUpsellAddons(addons, rankingByMenuItemId),
                    ditaUpsellAvailabilityByMenuItemId: availabilityByMenuItemId,
                    ditaUpsellAvailabilityBySku: availabilityBySku,
                    ditaUpsellLoading: false,
                    ditaUpsellError: '',
                });
            })
            .catch((error) => {
                if (!silent) {
                    this.setState({
                        ditaUpsellAddons: [],
                        ditaUpsellLoading: false,
                        ditaUpsellError: error && error.message ? error.message : 'Unable to load add-on recommendations.',
                    });
                }
            })
            .finally(() => {
                this.isDitaUpsellFetching = false;
            });
    };

    getDitaUpsellAddonsByCategory = (category) => {
        const normalizedCategory = `${category}`.toLowerCase();
        const { ditaUpsellAddons } = this.state;

        return (ditaUpsellAddons || [])
            .filter((item) => this.normalizeAddonCategory(item) === normalizedCategory);
    };

    getDitaFirstAvailableAddon = (categories) => {
        for (let index = 0; index < categories.length; index += 1) {
            const categoryAddons = this.getDitaUpsellAddonsByCategory(categories[index]);
            for (let addonIndex = 0; addonIndex < categoryAddons.length; addonIndex += 1) {
                if (this.isDitaUpsellCandidateAvailable(categoryAddons[addonIndex])) {
                    return categoryAddons[addonIndex];
                }
            }
        }

        return null;
    };

    getOrderDitaAttachmentCounts = (order) => {
        const items = order && Array.isArray(order.items) ? order.items : [];
        const isMainMealItem = (item) => {
            if (!item || item.item_type !== 'Item' || item.sku === 'Cutlery') {
                return false;
            }

            if (!item.item_group) {
                return true;
            }

            return `${item.item_group}`.endsWith('.0');
        };
        const isAddonCategory = (item, category) => {
            if (!item || item.item_type !== 'Addons') {
                return false;
            }

            return `${item.category_name || item.addon_category || ''}`.toLowerCase() === category;
        };

        return {
            meals: items.filter(isMainMealItem).length,
            drinks: items.filter((item) => isAddonCategory(item, 'drinks')).length,
            desserts: items.filter((item) => isAddonCategory(item, 'desserts')).length,
            snacks: items.filter((item) => isAddonCategory(item, 'snacks')).length,
        };
    };

    getDitaUpsellRecommendation = (order) => {
        if (!order || this.state.ditaUpsellLoading || this.state.ditaUpsellError) {
            return null;
        }

        const orderNumber = order.order_number;
        if (
            (orderNumber && this.state.ditaUpsellSkippedOrderNumbers[orderNumber]) ||
            (orderNumber && this.state.ditaUpsellCreatedOrderNumbers[orderNumber])
        ) {
            return null;
        }

        const isDineIn = this.isDineInOrder(order);
        const isTakeaway = this.isTakeawayOrder(order);
        if (!isDineIn && !isTakeaway) {
            return null;
        }

        const counts = this.getOrderDitaAttachmentCounts(order);
        if (counts.meals <= 0) {
            return null;
        }

        const hasDessertOrSnack = counts.desserts > 0 || counts.snacks > 0;
        let item = null;
        let reason = '';
        let script = '';

        if (isDineIn) {
            if (counts.drinks === 0) {
                item = this.getDitaFirstAvailableAddon(['drinks']);
                reason = 'No drink in Dine-In order';
                script = item ? `Nak tambah ${this.getAddonDisplayName(item)} sekali?` : '';
            } else if (counts.meals >= 2 && counts.drinks < counts.meals) {
                item = this.getDitaFirstAvailableAddon(['drinks']);
                reason = `${counts.meals} meals but only ${counts.drinks} drink${counts.drinks === 1 ? '' : 's'}`;
                script = item ? `Untuk meal kedua, nak tambah satu lagi ${this.getAddonDisplayName(item)}?` : '';
            } else if (!hasDessertOrSnack) {
                item = this.getDitaFirstAvailableAddon(['desserts', 'snacks']);
                reason = 'No dessert or snack in Dine-In order';
                script = item ? `Nak tambah ${this.getAddonDisplayName(item)} panas sekali?` : '';
            }
        }

        if (isTakeaway) {
            if (!hasDessertOrSnack) {
                item = this.getDitaFirstAvailableAddon(['desserts', 'snacks']);
                reason = 'No dessert or snack in Takeaway order';
                script = item ? `Nak tambah ${this.getAddonDisplayName(item)} panas sekali untuk bawa balik?` : '';
            } else if (counts.drinks === 0) {
                item = this.getDitaFirstAvailableAddon(['drinks']);
                reason = 'No drink in Takeaway order';
                script = item ? `Nak tambah ${this.getAddonDisplayName(item)} sekali untuk bawa balik?` : '';
            }
        }

        if (!item) {
            return null;
        }

        return {
            item,
            reason,
            script: this.formatDitaUpsellScript(order, script),
            price: this.formatDitaUpsellPrice(item),
        };
    };

    normalizeDitaCustomerPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) {
            return '';
        }

        const normalized = `${phoneNumber}`.trim();
        if (normalized.startsWith('+')) {
            return normalized;
        }

        if (normalized.startsWith('0')) {
            return `+6${normalized}`;
        }

        return `+${normalized}`;
    };

    buildDitaUpsellOrderPayload = (order, recommendation) => {
        const item = recommendation.item;
        const tableNumber = parseInt(order.table_number, 10);
        const pickUpType = this.isDineInOrder(order) ? 'DINE_IN' : 'TAKE_AWAY';

        return {
            invoice_number: 'terminal',
            payment_method: 'Pos',
            currency: order.currency || 'MYR',
            vendor_order_id: 'Pos',
            amount_paid: 0,
            customer_phone_no: this.normalizeDitaCustomerPhoneNumber(order.phone_number),
            customer_name: order.customer_name || order.customer_full_name || order.customer_display_name || '',
            menu_item: [
                {
                    menu_item_id: this.getAddonMenuItemId(item),
                    quantity: 1,
                },
            ],
            pick_up_type: pickUpType,
            reward_code: '',
            remark: `Outlet OS add-on order for ${order.order_number}`,
            custom_param: JSON.stringify({
                source: 'OUTLET_OS_DITA_UPSELL',
                original_order_number: order.order_number,
            }),
            transaction_date_time: Date.now(),
            table_number: Number.isFinite(tableNumber) ? tableNumber : null,
            provide_cutlery: false,
            drive_through_mode: false,
        };
    };

    handleSkipDitaUpsell = (orderNumber) => {
        if (!orderNumber) {
            return;
        }

        this.setState((state) => ({
            ditaUpsellSkippedOrderNumbers: {
                ...state.ditaUpsellSkippedOrderNumbers,
                [orderNumber]: true,
            },
        }));
    };

    handleCreateDitaUpsellOrder = (order, recommendation) => {
        const orderNumber = order && order.order_number;
        if (!orderNumber || !recommendation || !recommendation.item) {
            return;
        }

        this.setState((state) => ({
            ditaUpsellCreatingOrderNumbers: {
                ...state.ditaUpsellCreatingOrderNumbers,
                [orderNumber]: true,
            },
            actionErrorMessage: '',
        }));

        const {
            UserStore: { selectedHub },
        } = this.props;
        const hubCode = selectedHub && selectedHub.value;

        api.createPosOrder(this.buildDitaUpsellOrderPayload(order, recommendation), hubCode)
            .then((response) => {
                this.setState((state) => ({
                    ditaUpsellCreatingOrderNumbers: {
                        ...state.ditaUpsellCreatingOrderNumbers,
                        [orderNumber]: false,
                    },
                    ditaUpsellCreatedOrderNumbers: {
                        ...state.ditaUpsellCreatedOrderNumbers,
                        [orderNumber]: true,
                    },
                }));

                const createdOrderNumber = response && (response.order_number || response.orderId || response.order_id);
                toast.success(
                    createdOrderNumber
                        ? `Add-on order ${createdOrderNumber} placed and pending payment.`
                        : 'Add-on order placed and pending payment.',
                    { autoClose: 5000 }
                );

                const { LiveDispatchStore } = this.props;
                Promise.all([
                    LiveDispatchStore.handleGetDispatches(true),
                    LiveDispatchStore.handleGetDineInOrders(true),
                ]);
            })
            .catch((error) => {
                this.setState((state) => ({
                    ditaUpsellCreatingOrderNumbers: {
                        ...state.ditaUpsellCreatingOrderNumbers,
                        [orderNumber]: false,
                    },
                    actionErrorMessage: error && error.message ? error.message : 'Unable to create add-on order.',
                }));
            });
    };

    renderDitaUpsellCard = (order, variant = null) => {
        const recommendation = this.getDitaUpsellRecommendation(order);
        if (!recommendation) {
            return null;
        }

        const orderNumber = order.order_number;
        const itemName = this.getAddonDisplayName(recommendation.item);
        const isCreating = !!this.state.ditaUpsellCreatingOrderNumbers[orderNumber];
        const cardClassName = [
            'packing-v2__dita-upsell-card',
            variant ? `packing-v2__dita-upsell-card--${variant}` : null,
        ]
            .filter(Boolean)
            .join(' ');

        return (
            <div className={cardClassName} onClick={(event) => event.stopPropagation()}>
                <div className="packing-v2__dita-upsell-label">Suggested add-on</div>
                <div className="packing-v2__dita-upsell-reason">{recommendation.reason}</div>
                <div className="packing-v2__dita-upsell-item-row">
                    <span className="packing-v2__dita-upsell-item">{itemName}</span>
                    <span className="packing-v2__dita-upsell-price">{recommendation.price}</span>
                </div>
                <div className="packing-v2__dita-upsell-script">“{recommendation.script}”</div>
                <div className="packing-v2__dita-upsell-actions">
                    <button
                        className="packing-v2__dita-upsell-create"
                        onClick={() => this.handleCreateDitaUpsellOrder(order, recommendation)}
                        disabled={isCreating}
                    >
                        {isCreating
                            ? 'Creating add-on order...'
                            : `+ Create Add-On Order: ${itemName} ${recommendation.price}`}
                    </button>
                    <button
                        className="packing-v2__dita-upsell-skip"
                        onClick={() => this.handleSkipDitaUpsell(orderNumber)}
                        disabled={isCreating}
                    >
                        Customer said no
                    </button>
                </div>
            </div>
        );
    };

    getTopRowOrders = () => {
        const {
            LiveDispatchStore: { bags, dineInOrders },
        } = this.props;

        const dedupedOrders = {};

        const allOrders = []
            .concat(Array.isArray(bags) ? bags : [])
            .concat(Array.isArray(dineInOrders) ? dineInOrders : []);

        allOrders.forEach((order) => {
            if (this.shouldHideScheduledSelfPickup(order)) {
                return;
            }

            if (!order || !order.production_started_at) {
                return;
            }

            if (order && order.order_number && order.items && order.items.length > 0) {
                dedupedOrders[order.order_number] = order;
            }
        });

        return Object.values(dedupedOrders).sort((a, b) => {
            const aIsAgedInOutlet = this.isAgedDineInOrTakeaway(a);
            const bIsAgedInOutlet = this.isAgedDineInOrTakeaway(b);

            if (aIsAgedInOutlet !== bIsAgedInOutlet) {
                return aIsAgedInOutlet ? -1 : 1;
            }

            return this.getOrderTimestampMs(a) - this.getOrderTimestampMs(b);
        });
    };

    getOrderCardItems = (order) => {
        const items = order && Array.isArray(order.items) ? order.items : [];

        const meals = items
            .filter(
                (item) =>
                    (item.item_type === 'Item' && item.sku !== 'Cutlery') ||
                    item.item_group != null
            )
            .map((item) => {
                const group = item.item_group ? item.item_group.toString() : null;
                const isMainItem = !group || group.endsWith('.0');
                return {
                    ...item,
                    isMainItem,
                };
            })
            .sort((a, b) => {
                const parseGroup = (group) => {
                    if (group == null) {
                        return [Infinity, Infinity];
                    }
                    return group
                        .toString()
                        .split('.')
                        .map((part) => Number(part));
                };

                const [aMajor, aMinor] = parseGroup(a.item_group);
                const [bMajor, bMinor] = parseGroup(b.item_group);

                if (aMajor !== bMajor) {
                    return aMajor - bMajor;
                }

                return aMinor - bMinor;
            })
            .map((item, index, arr) => {
                const next = arr[index + 1];
                const lastItemOfGroup = !next || next.isMainItem === true;
                return {
                    ...item,
                    lastItemOfGroup,
                };
            });

        const cutlery = items.filter(
            (item) => item.item_type === 'Item' && item.sku === 'Cutlery'
        );
        const addons = items.filter(
            (item) =>
                item.item_type === 'Addons' &&
                item.category_name !== 'Drinks' &&
                item.item_group == null
        );
        const drinks = items.filter(
            (item) =>
                item.item_type === 'Addons' &&
                item.category_name === 'Drinks' &&
                item.item_group == null
        );

        return {
            meals,
            addons,
            drinks,
            cutlery,
        };
    };

    getOrderCardBrandName = (order) => {
        const salePlatform = order && order.sale_platform ? `${order.sale_platform}` : '';

        if (salePlatform.includes('GrabFood') && salePlatform.indexOf(' - GrabFood') > -1) {
            return salePlatform.split(' - GrabFood')[0];
        }

        if (salePlatform.includes('FoodPanda') && salePlatform.indexOf(' - FoodPanda') > -1) {
            return salePlatform.split(' - FoodPanda')[0];
        }

        if (salePlatform.includes('ShopeeFood') && salePlatform.indexOf(' - ShopeeFood') > -1) {
            return salePlatform.split(' - ShopeeFood')[0];
        }

        return null;
    };

    canCompleteOrderManually = (order) => {
        if (!order) {
            return false;
        }

        if (this.isAggregatorOrder(order)) {
            return false;
        }

        if (this.isDineInOrder(order) || this.isTakeawayOrder(order) || this.isSelfPickupOrder(order)) {
            return true;
        }

        return false;
    };

    getManualActionLabel = (order) => {
        if (this.isDineInOrder(order)) {
            return 'Mark Served';
        }

        return 'Mark Picked Up';
    };

    openOrderDetail = (order) => {
        this.setState({ selectedOrder: order, actionErrorMessage: '' });
    };

    closeOrderDetail = () => {
        this.setState({
            selectedOrder: null,
            actionLoading: false,
            actionErrorMessage: '',
            bagReprintLoading: false,
        });
    };


    closeButton = () => {
        return (
            <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onClick={() => toast.dismiss()}
            >
                <rect
                    x="1.06055"
                    width="19.7"
                    height="1.5"
                    transform="rotate(45 1.06055 0)"
                    fill="white"
                />
                <rect
                    width="19.7"
                    height="1.5"
                    transform="matrix(-0.707107 0.707107 0.707107 0.707107 13.9302 0)"
                    fill="white"
                />
            </svg>
        );
    };

    sendToastNotification = (toastId) => {
        const slide = cssTransition({
            enter: 'animate__animated animate__slideInDown',
            exit: 'animate__animated animate__slideOutUp',
        });

        toast.configure();
        toast(<CustomToastContainer />, {
            toastId,
            transition: slide,
            closeButton: this.closeButton,
            pauseOnHover: false,
            pauseOnFocusLoss: false,
            closeOnClick: false,
        });
    };

    handleBagReprint = () => {
        const { selectedOrder, bagReprintLoading } = this.state;
        const {
            ProductionStore,
            UserStore: { selectedHub },
        } = this.props;

        if (!selectedOrder || !selectedOrder.order_number || bagReprintLoading) {
            return;
        }

        const printerId = selectedHub && selectedHub.dispatchPrinterId;
        if (!printerId) {
            this.setState({
                actionErrorMessage: 'Dispatch printer not configured for bag reprint.',
            });
            return;
        }

        this.setState({ bagReprintLoading: true, actionErrorMessage: '' });

        ProductionStore.sendPrintRequest(
            {
                order_number: selectedOrder.order_number,
                print_mode: 'BAG_STICKER',
                printer_id: printerId,
            },
            () => {
                this.setState({ bagReprintLoading: false });
                this.sendToastNotification(`bag_reprint_${selectedOrder.order_number}`);
            },
            () => {
                this.setState({
                    bagReprintLoading: false,
                    actionErrorMessage: 'Unable to send bag reprint request.',
                });
            }
        );
    };

    handleMarkOrderAsDoneAsync = (orderNumber) => {
        const { LiveDispatchStore } = this.props;

        return new Promise((resolve, reject) => {
            LiveDispatchStore.handleMarkOrderAsDone(orderNumber, (status) => {
                if (status === 'SUCCESS') {
                    resolve();
                } else {
                    reject(new Error(status || 'Failed to mark order as done'));
                }
            });
        });
    };

    handleCompleteOrder = async () => {
        const {
            selectedOrder,
            actionLoading,
        } = this.state;

        if (!selectedOrder || actionLoading) {
            return;
        }

        const { LiveDispatchStore } = this.props;

        this.setState({ actionLoading: true, actionErrorMessage: '' });

        try {
            if (this.isSelfPickupOrder(selectedOrder) && selectedOrder.delivery_id) {
                await LiveDispatchStore.handleConfirmPickupOrder(
                    selectedOrder.delivery_id,
                    selectedOrder.order_number
                );
            } else {
                await this.handleMarkOrderAsDoneAsync(selectedOrder.order_number);
            }

            await Promise.all([
                LiveDispatchStore.handleGetDispatches(true),
                LiveDispatchStore.handleGetDineInOrders(true),
            ]);

            this.closeOrderDetail();
        } catch (error) {
            this.setState({
                actionLoading: false,
                actionErrorMessage: error && error.message ? error.message : 'Unable to complete order.',
            });
        }
    };

    renderDetailField = (label, value, valueClassName = '') => (
        <div className="packing-v2__detail-field">
            <div className="packing-v2__detail-label">{label}</div>
            <div className={`packing-v2__detail-value ${valueClassName}`.trim()}>{value}</div>
        </div>
    );

    renderDetailSection = (title, children) => (
        <section className="packing-v2__detail-section">
            <h3 className="packing-v2__detail-section-title">{title}</h3>
            <div className="packing-v2__detail-section-grid">
                {children}
            </div>
        </section>
    );

    renderDetailModal = () => {
        const { selectedOrder, actionLoading, actionErrorMessage, bagReprintLoading } = this.state;

        if (!selectedOrder) {
            return null;
        }

        const { meals, addons, drinks, cutlery } = this.getOrderCardItems(selectedOrder);
        const brandName = this.getOrderCardBrandName(selectedOrder);
        const paymentDone = this.isPaymentDone(selectedOrder);
        const statusNeedsAttention = this.shouldHighlightOrderStatus(selectedOrder);
        const hasOrderItems =
            meals.length > 0 || addons.length > 0 || drinks.length > 0 || cutlery.length > 0;
        const orderType = this.getOrderType(selectedOrder);
        const orderStatus = selectedOrder.order_status ? selectedOrder.order_status.toUpperCase() : '-';
        const carDescription = this.getPanduCarDescription(selectedOrder);
        const whatsappUrl = this.getWhatsappUrl(selectedOrder.phone_number);
        const whatsappQrUrl = this.getWhatsappQrUrl(selectedOrder.phone_number);
        const isMobile = isMobileViewport();
        const shouldShowRiderSection = !(
            this.isDineInOrder(selectedOrder) ||
            this.isSelfPickupOrder(selectedOrder) ||
            selectedOrder.order_mode === 'PICK_UP'
        );

        return (
            <Modal className="packing-v2__detail-modal" height={'80vh'} onBackdropClicked={this.closeOrderDetail}>
                <div className="packing-v2__detail">
                    <div className="packing-v2__detail-header">
                        <div className="packing-v2__detail-title-row">
                            <div>
                                <div className="packing-v2__detail-title-label">{orderType.label}</div>
                                <h2 className="packing-v2__detail-title">
                                    {this.getSummaryOrderNumber(selectedOrder)}
                                </h2>
                            </div>
                            <button
                                className="packing-v2__detail-secondary-action"
                                onClick={this.handleBagReprint}
                                disabled={bagReprintLoading}
                            >
                                {bagReprintLoading
                                    ? 'Reprinting bag sticker...'
                                    : 'Reprint bag sticker'}
                            </button>
                        </div>
                        <div className="packing-v2__detail-header-actions">
                            {/* Temporarily always show this button. Restore the old gate below to limit it again.
                            {this.canCompleteOrderManually(selectedOrder) && (
                                <button
                                    className="packing-v2__detail-primary-action packing-v2__detail-primary-action--header"
                                    onClick={this.handleCompleteOrder}
                                    disabled={actionLoading}
                                >
                                    {actionLoading
                                        ? 'Updating...'
                                        : this.getManualActionLabel(selectedOrder)}
                                </button>
                            )}
                            */}
                            <button
                                className="packing-v2__detail-primary-action packing-v2__detail-primary-action--header"
                                onClick={this.handleCompleteOrder}
                                disabled={actionLoading}
                            >
                                {actionLoading
                                    ? 'Updating...'
                                    : this.getManualActionLabel(selectedOrder)}
                            </button>
                            <button
                                className="packing-v2__detail-close"
                                onClick={this.closeOrderDetail}
                            >
                                Close
                            </button>
                            
                            {actionErrorMessage && (
                                <p className="packing-v2__detail-error">{actionErrorMessage}</p>
                            )}
                        </div>
                    </div>

                    <div className="packing-v2__detail-body">
                        <div className="packing-v2__detail-meta">
                            {this.renderDetailSection('Order Details', (
                                <>
                                    {this.renderDetailField('Type', orderType.label)}
                                    {this.renderDetailField('Bag / Table', this.getOrderDisplayNumber(selectedOrder))}
                                    {this.renderDetailField('Order Number', selectedOrder.order_number || '-')}
                                    {this.renderDetailField('Order Time', this.formatOrderTime(selectedOrder))}
                                    {selectedOrder.aggregator_order_id
                                        ? this.renderDetailField('Aggregator Order', selectedOrder.aggregator_order_id)
                                        : null}
                                    {this.renderDetailField(
                                        'Status',
                                        <span
                                            className={`packing-v2__detail-status-pill ${
                                                statusNeedsAttention
                                                    ? 'packing-v2__detail-status-pill--danger'
                                                    : 'packing-v2__detail-status-pill--success'
                                            }`}
                                        >
                                            {orderStatus}
                                        </span>
                                    )}
                                </>
                            ))}

                            {this.renderDetailSection('Customer', (
                                <>
                                    {this.renderDetailField('Name', this.getSummaryCustomerName(selectedOrder))}
                                    {this.renderDetailField('Phone Number', selectedOrder.phone_number || '-')}
                                    {isMobile && whatsappUrl ? (
                                        <div className="packing-v2__detail-field packing-v2__detail-field--wide">
                                            <div className="packing-v2__detail-label">WHATSAPP CUSTOMER</div>
                                            <a
                                                className="packing-v2__whatsapp-button"
                                                href={whatsappUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                WhatsApp the customer
                                            </a>
                                        </div>
                                    ) : whatsappQrUrl ? (
                                        <div className="packing-v2__detail-field packing-v2__detail-field--wide">
                                            <div className="packing-v2__detail-label">SCAN TO WHATSAPP CUSTOMER</div>
                                            <a
                                                className="packing-v2__whatsapp-qr-link"
                                                href={whatsappUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <img
                                                    className="packing-v2__whatsapp-qr"
                                                    src={whatsappQrUrl}
                                                    alt={`WhatsApp QR for ${selectedOrder.phone_number}`}
                                                />
                                            </a>
                                        </div>
                                    ) : null}
                                </>
                            ))}

                            {selectedOrder.car_details && this.renderDetailSection('Car', (
                                <>
                                    {this.renderDetailField('Vehicle', carDescription || '-')}
                                    {this.renderDetailField(
                                        'Number Plate',
                                        <span className="packing-v2__detail-plate">
                                            {selectedOrder.car_details.number_plate || '-'}
                                        </span>
                                    )}
                                </>
                            ))}

                            {this.renderDetailSection('Payment', (
                                <>
                                    {this.renderDetailField('Method', selectedOrder.payment_method || '-')}
                                    {this.renderDetailField(
                                        'Payment Done?',
                                        <span
                                            className={`packing-v2__detail-payment-state ${
                                                paymentDone
                                                    ? 'packing-v2__detail-payment-state--paid'
                                                    : 'packing-v2__detail-payment-state--pending'
                                            }`}
                                        >
                                            <img
                                                className="packing-v2__payment-status-icon"
                                                src={
                                                    paymentDone
                                                        ? 'https://image.popcontent.link/green_checkmark.svg'
                                                        : 'https://image.popcontent.link/red_cross.svg'
                                                }
                                                alt={paymentDone ? 'Payment complete' : 'Payment pending'}
                                            />
                                            {paymentDone ? 'Yes' : 'No'}
                                        </span>
                                    )}
                                    {this.renderDetailField('Net Total', this.getNetTotalDisplay(selectedOrder))}
                                </>
                            ))}

                            {shouldShowRiderSection && this.renderDetailSection('Rider', (
                                <>
                                    {this.renderDetailField('Name', selectedOrder.rider_name || '-')}
                                    {this.renderDetailField('Phone Number', selectedOrder.rider_phone_no || '-')}
                                </>
                            ))}
                        </div>

                        <div className="packing-v2__detail-items">
                            <h3 className="packing-v2__detail-items-title">Items</h3>
                            {hasOrderItems === false ? (
                                <p className="packing-v2__detail-empty">No items available.</p>
                            ) : (
                                <div className="packing-v2__detail-items-list">
                                    <div className="order-items-skus packing-v2__detail-card-items">
                                        {brandName ? (
                                            <div
                                                className="sku-item__item"
                                                style={{
                                                    justifyContent: 'center',
                                                    background: '#4154FF',
                                                    color: 'white',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <p className="aggregator_name">
                                                    {brandName.toUpperCase()}
                                                </p>
                                            </div>
                                        ) : null}

                                        {meals.map((item, index) => (
                                            <SKUItem
                                                key={`${item.sku}_${item.incremental_number}`}
                                                item={{ ...item, order_number: selectedOrder.order_number }}
                                                type="meals"
                                                isFirstItem={index === 0}
                                                isLastItem={index === meals.length - 1}
                                                isLastCategory={addons.length === 0 && drinks.length === 0 && cutlery.length === 0}
                                                isIncomingOrder={false}
                                                isPackingV2={true}
                                                disableActions={false}
                                            />
                                        ))}

                                        {addons.map((item, index) => (
                                            <SKUItem
                                                key={`${item.sku}_${item.incremental_number}`}
                                                item={{ ...item, order_number: selectedOrder.order_number }}
                                                type="addons"
                                                isFirstItem={index === 0}
                                                isLastItem={index === addons.length - 1}
                                                isLastCategory={drinks.length === 0 && cutlery.length === 0}
                                                isIncomingOrder={false}
                                                isPackingV2={true}
                                                disableActions={false}
                                            />
                                        ))}

                                        {drinks.map((item, index) => (
                                            <SKUItem
                                                key={`${item.sku}_${item.incremental_number}`}
                                                item={{ ...item, order_number: selectedOrder.order_number }}
                                                type="drinks"
                                                isFirstItem={index === 0}
                                                isLastItem={index === drinks.length - 1}
                                                isLastCategory={cutlery.length === 0}
                                                isIncomingOrder={false}
                                                isPackingV2={true}
                                                disableActions={false}
                                            />
                                        ))}

                                        {cutlery.map((item, index) => (
                                            <SKUItem
                                                key={`${item.sku}_${item.incremental_number}`}
                                                item={{ ...item, order_number: selectedOrder.order_number }}
                                                type="cutlery"
                                                isFirstItem={index === 0}
                                                isLastItem={index === cutlery.length - 1}
                                                isLastCategory={true}
                                                isIncomingOrder={false}
                                                isPackingV2={true}
                                                disableActions={false}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {this.renderDitaUpsellCard(selectedOrder)}
                        </div>
                    </div>
                </div>
            </Modal>
        );
    };

    render() {
        const { LiveDispatchStore, UserStore } = this.props;

        const {
            loading,
            itemImage,
            itemScanLoading,
            scannedItem,
            scannedItemSKU,
            scannedItemError,
            showScannedItemOnLiveDispatch,
            originalItemImageLink,
            updateOrderData,
            instaServiceProductions,
            recentlyClosedAggregatorStores,
            driveThroughAlerts,
            carArrivedAlerts,
            needHelpAlerts,
        } = LiveDispatchStore;

        const {
            isCurrentPageFullScreen,
            handleSetFullscreen,
            handleExitFullscreen,
        } = UserStore;

        const { lastScannedItem } = this.state;

        const topRowOrders = this.getTopRowOrders();
        const hasPendingInstaServiceTopup = this.hasPendingInstaServiceTopup(
            instaServiceProductions
        );
        const alertModalOpen =
            (recentlyClosedAggregatorStores && recentlyClosedAggregatorStores.length > 0) ||
            (driveThroughAlerts && driveThroughAlerts.length > 0) ||
            (carArrivedAlerts && carArrivedAlerts.length > 0) ||
            (needHelpAlerts && needHelpAlerts.length > 0);

        if (recentlyClosedAggregatorStores && recentlyClosedAggregatorStores.length > 0) {
            audio.play();
        } else if (driveThroughAlerts && driveThroughAlerts.length > 0) {
            driveThroughAudio.play();
        }

        if (loading) {
            return <Loading />;
        }

        return (
            <Fullscreen
                enabled={isCurrentPageFullScreen}
                onChange={(isFullscreen) => {
                    if (isFullscreen) {
                        handleSetFullscreen(isFullscreen);
                    } else {
                        handleExitFullscreen();
                    }
                }}
            >
                <div
                    onClick={this.handleClick}
                    style={
                        isCurrentPageFullScreen
                            ? {
                                height: '100vh',
                                backgroundColor: 'white',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }
                            : {
                                height: 'calc(100vh - 75px)',
                                backgroundColor: 'white',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }
                    }
                >
                    <Helmet>
                        <title>Packing | Outlet OS | Pop Meals</title>
                    </Helmet>

                    <LiveDispatchHeader />

                    <div className="packing-v2">
                        <div className={`${base}__webcam-area`}>
                            <Webcam
                                audio={false}
                                ref={this.webcamRef}
                                screenshotFormat="image/jpeg"
                                className="webcam"
                                screenshotQuality={1}
                                forceScreenshotSourceSize={true}
                                videoConstraints={{
                                    height: 1080,
                                    width: 1920,
                                }}
                            />
                        </div>

                        <div className="packing-v2__summary">
                            <div className="packing-v2__summary-title-row">
                                <div className="packing-v2__summary-title-group">
                                    <h1 className="packing-v2__summary-title">All Active Orders</h1>
                                </div>
                            </div>

                            <div className="packing-v2__summary-row">
                                {topRowOrders.length === 0 && (
                                    <p className="packing-v2__summary-empty">No active orders.</p>
                                )}

                                {topRowOrders.map((order) => {
                                    const orderType = this.getOrderType(order);
                                    const isComplete = this.isOrderFullyScanned(order);
                                    const amountDue = this.getOrderAmountDue(order);
                                    const aggregatorLogo = this.getAggregatorLogo(order);
                                    const aggregatorPlatform = this.getAggregatorPlatform(order);
                                    const isAggregatorOrder = this.isAggregatorOrder(order);
                                    const isPanduOrder = this.isPanduOrder(order);
                                    const panduCarDescription = this.getPanduCarDescription(order);
                                    const panduNumberPlate =
                                        order && order.car_details && order.car_details.number_plate
                                            ? order.car_details.number_plate
                                            : '';
                                    const isPanduCustomerWaiting =
                                        this.isPanduMasukCustomerWaiting(order);
                                    const isAgedInOutlet =
                                        !isPanduOrder && !isComplete && this.isAgedDineInOrTakeaway(order);
                                    const isPriorityOrder =
                                        orderType.key === 'dine-in' || orderType.key === 'takeaway';
                                    const typeBadgeClassNames = [
                                        'packing-v2__type-badge',
                                        `packing-v2__type-badge--${orderType.key}`,
                                        orderType.key === 'aggregator' && aggregatorPlatform
                                            ? `packing-v2__type-badge--${aggregatorPlatform}`
                                            : null,
                                    ]
                                        .filter(Boolean)
                                        .join(' ');

                                    return (
                                        <div
                                            key={order.order_number}
                                            className={`packing-v2__summary-card ${
                                                isComplete ? 'is-complete' : ''
                                            } ${isAgedInOutlet ? 'is-aged-inoutlet' : ''} ${
                                                isPanduCustomerWaiting ? 'is-pandu-waiting' : ''
                                            } ${
                                                amountDue != null ? 'has-amount-due' : ''
                                            }`}
                                            onClick={() => this.openOrderDetail(order)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    this.openOrderDetail(order);
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <div className="packing-v2__summary-card-header">
                                                <div className="packing-v2__summary-card-header-left">
                                                    {aggregatorLogo && (
                                                        <img
                                                            className="packing-v2__summary-platform-logo"
                                                            src={aggregatorLogo.src}
                                                            alt={`${aggregatorLogo.alt} logo`}
                                                        />
                                                    )}
                                                    {isPanduOrder && (
                                                        <img
                                                            className="packing-v2__summary-car-icon"
                                                            src="https://image.popcontent.link/car.svg"
                                                            alt="Car"
                                                        />
                                                    )}
                                                    <span
                                                        className={typeBadgeClassNames}
                                                    >
                                                        {orderType.label}
                                                    </span>
                                                </div>
                                                <div className="packing-v2__summary-card-header-right">
                                                    {isPriorityOrder && (
                                                        <span className="packing-v2__priority-badge">
                                                            Priority
                                                        </span>
                                                    )}
                                                    {isComplete && (
                                                        <span className="packing-v2__complete-badge">
                                                            Fully Scanned
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="packing-v2__summary-number">
                                                {this.getSummaryOrderNumber(order)}
                                                {isPanduCustomerWaiting && (
                                                    <span className="packing-v2__summary-waiting-badge">
                                                        CUSTOMER WAITING OUTSIDE
                                                    </span>
                                                )}
                                            </div>

                                            {isAggregatorOrder ? (
                                                <>
                                                    <div className="packing-v2__summary-label">
                                                        Aggregator Order
                                                    </div>
                                                    <div className="packing-v2__summary-value">
                                                        {order.aggregator_order_id || '-'}
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="packing-v2__summary-label">Customer</div>
                                                    <div
                                                        className="packing-v2__summary-value packing-v2__summary-value--customer"
                                                        title={this.getSummaryCustomerName(order)}
                                                    >
                                                        {this.getSummaryCustomerName(order)}
                                                    </div>
                                                </>
                                            )}

                                            {isPanduOrder && (panduNumberPlate || panduCarDescription) && (
                                                <div className="packing-v2__summary-car-details">
                                                    {panduNumberPlate && (
                                                        <span className="packing-v2__summary-number-plate">
                                                            {panduNumberPlate}
                                                        </span>
                                                    )}
                                                    {panduCarDescription && (
                                                        <span className="packing-v2__summary-car-description">
                                                            {panduCarDescription}
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {amountDue != null && (
                                                <span className="packing-v2__summary-amount-due-badge">
                                                    {this.formatCurrencyAmount(amountDue)} due
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="packing-v2__detail-sections display--flex bg--white col-sm-12 p0">
                            {hasPendingInstaServiceTopup && (
                                <div className="packing-v2__instaservice-column">
                                    <InstaServiceProduction
                                        instaServiceProductions={instaServiceProductions}
                                    />
                                </div>
                            )}

                            <div
                                className={`${base}__dine-in-orders-container packing-v2__inoutlet-column`}
                                style={{
                                    paddingRight: '10px',
                                    borderRight: '3px solid #e5e5e5',
                                    width: 'max-content',
                                    minWidth: 'max-content',
                                    flex: '0 0 auto',
                                }}
                            >
                                <Orders
                                    inOutletOrders={true}
                                    routeSectionVisible={false}
                                    lastScannedItem={lastScannedItem}
                                    isPackingV2={true}
                                    renderOrderAddon={(order) => this.renderDitaUpsellCard(order, 'live-dispatch')}
                                />
                            </div>

                            <hr className="packing-v2__section-divider" />

                            <div className={`${base}__dine-in-orders-container packing-v2__delivery-column`}>
                                <Orders
                                    inOutletOrders={false}
                                    routeSectionVisible={false}
                                    lastScannedItem={lastScannedItem}
                                    isPackingV2={true}
                                />
                            </div>
                        </div>
                    </div>

                    <BarcodeReader onError={() => {}} onScan={this.handleScanWrapper} />

                    <ScanningBox
                        show={showScannedItemOnLiveDispatch}
                        scannedItem={scannedItem}
                        scannedItemSKU={scannedItemSKU}
                        scannedItemError={scannedItemError}
                        handleScan={this.handleScanWrapper}
                        loading={itemScanLoading}
                        originalItemImageLink={originalItemImageLink}
                        itemImage={itemImage}
                        isCurrentPageFullScreen={isCurrentPageFullScreen}
                    />

                    <OrderWebSocket
                        updateOrderData={updateOrderData}
                        onConnectionChange={this.handleWebSocketConnectionChange}
                    />

                    {recentlyClosedAggregatorStores && recentlyClosedAggregatorStores.length > 0 && (
                        <Modal className="packing-v2__store-alert-modal" width={'949px'}>
                            <div className="packing-v2__store-alert">
                                <p className="packing-v2__store-alert-title">
                                    Alert - Store Closed
                                </p>

                                <div className="packing-v2__store-alert-list">
                                    {recentlyClosedAggregatorStores.map((store) => (
                                        <div className="packing-v2__store-alert-row row" key={store}>
                                            <div className="packing-v2__store-alert-name col-xs-8">{store}</div>
                                            <div className="packing-v2__store-alert-action col-xs-4">
                                                <button
                                                    className="packing-v2__store-alert-open btn btn-primary width--150px text--bold"
                                                    onClick={() => this.handleClickOpen(store)}
                                                >
                                                    Open Now
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <p className="packing-v2__store-alert-copy">
                                    These stores are closed. Please re-open them immediately
                                </p>

                                <div className={`${base}__confirmation_btns packing-v2__store-alert-confirmation`}>
                                    <button
                                        className="btn btn-secondary ms flex-1"
                                        onClick={() => {
                                            LiveDispatchStore.recentlyClosedAggregatorStores = [];
                                            audio.pause();
                                        }}
                                        style={{
                                            backgroundColor: 'black',
                                            width: '200px',
                                            height: '70px',
                                            fontSize: '24px',
                                        }}
                                    >
                                        OK
                                    </button>
                                </div>
                            </div>
                        </Modal>
                    )}
                    {recentlyClosedAggregatorStores.length === 0 &&
                        driveThroughAlerts.length > 0 && (
                        <Modal width={'949px'}>
                            <div style={{ textAlign: 'center' }}>
                                <p
                                    style={{
                                        fontSize: '72px',
                                        fontWeight: '700',
                                        color: '#FB275F',
                                    }}
                                >
                                    CAR IN DRIVE THROUGH
                                </p>

                                <br />
                                <br />
                                <br />

                                <div className={`${base}__confirmation_btns`}>
                                    <button
                                        className="btn btn-secondary ms flex-1"
                                        onClick={() => {
                                            LiveDispatchStore.handleAcknowledgeDriveThroughAlert();
                                            driveThroughAudio.pause();
                                            driveThroughAudio.currentTime = 0;
                                        }}
                                        style={{
                                            backgroundColor: 'black',
                                            width: '200px',
                                            height: '70px',
                                            fontSize: '24px',
                                        }}
                                    >
                                        OK
                                    </button>
                                </div>
                            </div>
                        </Modal>
                    )}
                    {recentlyClosedAggregatorStores.length === 0 &&
                        driveThroughAlerts.length === 0 &&
                        carArrivedAlerts.length > 0 && (
                        <Modal width={'949px'}>
                            <div style={{ textAlign: 'center' }}>
                                <p
                                    style={{
                                        fontSize: '72px',
                                        fontWeight: '700',
                                        color: '#FB275F',
                                    }}
                                >
                                    PANDU MASUK
                                    <br />
                                    CUSTOMER ARRIVED
                                </p>
                                <br />
                                <p
                                    style={{
                                        fontSize: '32px',
                                        marginBottom: '20px',
                                    }}
                                >
                                    Go outside now and find the car with hazard light on
                                </p>
                                <div style={{ fontSize: '36px', fontWeight: '700' }}>
                                    <div className="col-sm-6 text--left">Customer Name</div>
                                    <div className="col-sm-6 text--left">
                                        {carArrivedAlerts[0].customer_name}
                                    </div>
                                    <div className="col-sm-6 text--left">Bag Number</div>
                                    <div className="col-sm-6 text--left">
                                        {carArrivedAlerts[0].bag_number}
                                    </div>
                                    {carArrivedAlerts[0].car_details && (
                                        <div>
                                            <div className="col-sm-6 text--left">Make</div>
                                            <div className="col-sm-6 text--left">
                                                {carArrivedAlerts[0].car_details.make}
                                            </div>
                                            <div className="col-sm-6 text--left">Color</div>
                                            <div className="col-sm-6 text--left">
                                                {carArrivedAlerts[0].car_details.color}
                                            </div>
                                            <div className="col-sm-6 text--left">Number Plate</div>
                                            <div className="col-sm-6 text--left">
                                                {carArrivedAlerts[0].car_details.number_plate}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className={`${base}__confirmation_btns`}>
                                    <button
                                        className="btn btn-secondary ms flex-1"
                                        onClick={() => {
                                            LiveDispatchStore.carArrivedAlerts = [];
                                            LiveDispatchStore.rememberPanduMasukCustomerWaiting(
                                                carArrivedAlerts[0].bag_number
                                            );
                                        }}
                                        style={{
                                            backgroundColor: 'black',
                                            width: '400px',
                                            height: '70px',
                                            fontSize: '24px',
                                        }}
                                    >
                                        OK - GOING OUTSIDE NOW
                                    </button>
                                </div>
                            </div>
                        </Modal>
                    )}
                    {recentlyClosedAggregatorStores.length === 0 &&
                        driveThroughAlerts.length === 0 &&
                        carArrivedAlerts.length === 0 &&
                        needHelpAlerts.length > 0 && (
                        <Modal width={'949px'}>
                            <div style={{ textAlign: 'center' }}>
                                <p
                                    style={{
                                        fontSize: '60px',
                                        fontWeight: '700',
                                        color: '#FB275F',
                                    }}
                                >
                                    Customer needs help!
                                    <br />
                                    Go to iPad Kiosk now
                                </p>
                                <br />
                                <div className={`${base}__confirmation_btns`}>
                                    <button
                                        className="btn btn-secondary ms flex-1"
                                        onClick={() => {
                                            LiveDispatchStore.needHelpAlerts = [];
                                        }}
                                        style={{
                                            backgroundColor: 'black',
                                            width: '400px',
                                            height: '70px',
                                            fontSize: '24px',
                                        }}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </Modal>
                    )}
                    {!alertModalOpen && this.renderDetailModal()}
                </div>
            </Fullscreen>
        );
    }
}

export default PackingV2;
